// สัญญาของใบเสร็จที่ POS ส่งขึ้นไปเก็บ
//
// ของเดิมประกาศไว้แค่ 8 field แล้วไม่มีใครใช้เป็น type จริง (ReceiptItem กับ
// ReceiptPayment ไม่เคยถูกประกาศเลย) ส่วน ReceiptBuilder ก็ประกาศ receipt เป็น any
// ไฟล์นี้เขียนใหม่ให้ตรงกับของที่ส่งขึ้นไปจริง เพื่อให้ allowlist ใน
// ReceiptBuilder.withItems() มีที่อ้างอิงที่เดียว — ดู xtable-project/nuxt3-pos-v3#60

// ค่าเงินที่เก็บเป็น 3 ช่อง [mode, rate, amount]
// ช่อง 2 (amount) คือช่องที่ฝั่งรายงานอ่านจริงทุกที่
export type MoneyTuple = [number, number, number]

export interface ReceiptItemOption {
    productId: string
    name: string
    price: number
    qty: number
}

export interface ReceiptItem {
    // ---- ตัวที่มีคนอ่านจริง ----
    productId: string
    name: string
    price: number
    qty: number
    comments?: string
    addOnPrice: number
    extendedPrice: number
    extendedPriceWithDiscount: number
    subTotal: number
    amount: number
    dc1: MoneyTuple
    dc2: MoneyTuple
    sc: MoneyTuple
    cc: MoneyTuple
    tax: MoneyTuple
    // มีเฉพาะรายการที่มี topping จริง ไม่มี topping = ไม่มี key นี้ ไม่ใช่ [] ว่าง
    // ใบเก่าที่เก็บไว้แล้วยังมี [] หรือ null ติดอยู่ ฝั่งอ่านต้องทนทั้งสามแบบ
    options2?: Array<ReceiptItemOption>

    // ---- เลิกเก็บแล้ว ใบเสร็จใหม่จะไม่มี field เหล่านี้ ----
    //
    // ทั้งหมดเป็น optional เพราะใบเสร็จ "เก่า" ที่เก็บไว้แล้วยังมีอยู่ และเราไม่
    // backfill ไม่ migrate ของเก่า โค้ดที่รับ ReceiptItem จึงต้องทนได้ทั้งสองแบบ
    // ห้ามเขียนโค้ดที่อ่านค่าเหล่านี้แบบไม่มี guard
    //
    // timestamp  เวลาสั่งรายรายการ — เวลาเปิดโต๊ะอยู่ที่ metadata.startDate
    //            เวลาปิดบิลอยู่ที่ createdAt วันที่รายงานอยู่ที่ businessDate
    //            ReceiptBuilder ยังอ่านของ "ตะกร้า" เพื่อเรียงรายการกับตั้ง
    //            startDate แต่ไม่เก็บลงใบเสร็จ
    // discountItem  ซ้ำกับ dc1 — คอลัมน์ Discount Item บนแดชบอร์ดผูกกับ dc1
    //               ไม่ใช่ตัวนี้ และไม่มี sheet ไหนพิมพ์ตัวนี้ออกมา
    // uuid/jobId    ใช้ไล่กลับไปหา job ต้นทาง ซึ่งไม่มีใครทำ
    // employeeId/employeeName  คนที่สั่งรายการนั้น — ระดับบิลมี employee อยู่แล้ว
    timestamp?: string
    discountItem?: MoneyTuple | [number, number]
    uuid?: string
    jobId?: string
    employeeId?: string
    employeeName?: string
}

// field ที่อนุญาตให้ขึ้นไปกับ item ของใบเสร็จ
// ต้องเรียงตรงกับ ReceiptItem ด้านบน อะไรที่ไม่อยู่ในลิสต์นี้จะถูกตัดทิ้ง
export const RECEIPT_ITEM_FIELDS: string[] = [
    "productId",
    "name",
    "price",
    "qty",
    "comments",
    "addOnPrice",
    "extendedPrice",
    "extendedPriceWithDiscount",
    "subTotal",
    "amount",
    "dc1",
    "dc2",
    "sc",
    "cc",
    "tax",
    "options2",

    // ยกเลิกแล้ว ไม่ต้องสร้างค่าเหล่านี้ตอน build ใบเสร็จอีกต่อไป
    // ปลดคอมเมนต์เมื่อไร มันจะกลับขึ้นไปกับใบเสร็จทันที
    // "timestamp",
    // "discountItem",
    // "uuid",
    // "jobId",
    // "employeeId",
    // "employeeName",
]

export const RECEIPT_ITEM_OPTION_FIELDS: string[] = [
    "productId",
    "name",
    "price",
    "qty",
]

export interface ReceiptPaymentLine {
    paymentId?: string
    name: string
    amount: number
    amountInput?: number
    isEntertain?: boolean
    entertainMode?: string | null
    entertainAmount?: number
}

// ระดับบิล dc1/dc2/sc/cc/tax เป็นตัวเลขธรรมดา ไม่ใช่ array สามช่องแบบระดับ item
export interface ReceiptPayment {
    grandTotal: number
    list: Array<ReceiptPaymentLine>
    received: number
    change: number
    subTotal: number
    dc1: number
    sc: number
    dc2: number
    cc: number
    tax: number
    rounding: number
    beforeTax?: number
    note: string
    isEntertain?: boolean
}

export interface ReceiptEmployee {
    name: string
    employeeId: string
}

export interface ReceiptMetadata {
    startDate: string
    endDate: string
    guests: number
    type: string
    priceMode: string
    shift: string
    // ฝั่ง api-report-service ใช้กรองรายงานตาม tag — ห้ามตัด
    tags?: any
    room?: any
    channel?: { channelId?: string; name: string }
    computerId?: string | null
}

export interface ReceiptVoided {
    timestamp: string
    reason: string
    employee: ReceiptEmployee
}

export interface Receipt {
    table: string
    receiptId: string
    storeId: string
    bill: string | number
    createdAt: string
    businessDate: string
    live: boolean
    items: Array<ReceiptItem>
    employee: ReceiptEmployee
    payment: ReceiptPayment
    metadata: ReceiptMetadata

    // hash ใช้เป็นคีย์ index ยอดสดบน RTDB และใช้จับคู่ VOID_RECEIPT กับ PAY job
    hash?: string
    voided?: ReceiptVoided
    // api-report-service สร้างคอลัมน์ Excel ของ pivot จากคีย์ในก้อนนี้แบบ dynamic
    categoriesSummary?: Record<string, any>

    // backend ใส่ให้ตอนอ่านกลับมา ฝั่ง POS ไม่ได้เขียนขึ้นไป
    key1?: string
    key2?: string

    [key: string]: any
}
